import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Form,
  Input,
  Popover,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { withDevModalFilePath } from '../../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/admin/subscriptions/SubscriptionListPage.tsx');
import {
  SettingOutlined,
  InfoCircleOutlined,
  PauseCircleOutlined,
  SwapOutlined,
  FileAddOutlined,
  ReloadOutlined,
  SaveOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { trpc } from '../../../lib/api';
import {
  DEFAULT_COLUMN_KEYS,
  STATUS_LABELS,
  calculateSummary,
  formatCurrency,
  formatDate,
  formatDateShort,
  type ContractListFilters,
  type SavedFilterSet,
  type SubscriptionContractRow,
  type SubscriptionUsage,
} from './subscription-helpers';
import { ENTERPRISE_PREMIUM_ENABLED } from '@shared/config/subscription-plans';

const { Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const STORAGE_KEYS = {
  filters: 'admin-subscription-saved-filters',
  columns: 'admin-subscription-visible-columns',
};

const PLAN_LABELS: Record<string, string> = {
  starter: 'スターター',
  standard: 'スタンダード',
  professional: 'プロフェッショナル',
  enterprise: 'エンタープライズ',
  enterprise_premium: 'エンタープライズ・プレミアム',
  premium: 'プレミアム',
  free: 'フリープラン',
};

const BILLING_CYCLE_LABELS: Record<string, string> = {
  monthly: '月次',
  quarterly: '四半期',
  yearly: '年次',
};

const PAYMENT_PROVIDER_LABELS: Record<string, string> = {
  stripe: 'Stripe',
  manual: '手動請求',
  bank_transfer: '銀行振込',
};

const STATUS_OPTIONS = Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label }));

const PLAN_OPTIONS = Object.entries(PLAN_LABELS).map(([value, label]) => ({ value, label }));
const AVAILABLE_PLAN_OPTIONS = PLAN_OPTIONS.filter(
  (option) => ENTERPRISE_PREMIUM_ENABLED || option.value !== 'enterprise_premium',
);

const BILLING_CYCLE_OPTIONS = Object.entries(BILLING_CYCLE_LABELS).map(([value, label]) => ({ value, label }));

const PAYMENT_PROVIDER_OPTIONS = Object.entries(PAYMENT_PROVIDER_LABELS).map(([value, label]) => ({ value, label }));

const COLUMN_OPTIONS = [
  { key: 'company', label: '会社' },
  { key: 'plan', label: 'プラン' },
  { key: 'status', label: 'ステータス' },
  { key: 'mrr', label: 'MRR' },
  { key: 'billingCycle', label: '課金サイクル' },
  { key: 'nextInvoice', label: '次回請求日' },
  { key: 'paymentProvider', label: '支払いプロバイダー' },
  { key: 'usage', label: '最新利用量' },
  { key: 'flags', label: 'フラグ' },
  { key: 'updatedAt', label: '最終更新' },
];

function loadFromLocalStorage<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') {
    return fallback;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    return JSON.parse(raw) as T;
  } catch (error) {
    console.warn('Failed to parse localStorage value', key, error);
    return fallback;
  }
}

function persistToLocalStorage<T>(key: string, value: T) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn('Failed to persist localStorage value', key, error);
  }
}

function usePersistedState<T>(key: string, initialValue: T): [T, (value: T) => void] {
  const [state, setState] = useState<T>(() => loadFromLocalStorage(key, initialValue));

  const updateState = useCallback(
    (value: T) => {
      setState(value);
      persistToLocalStorage(key, value);
    },
    [key],
  );

  return [state, updateState];
}

function mapRow(row: SubscriptionContractRow): SubscriptionContractRow {
  return {
    ...row,
    mrr: typeof row.mrr === 'string' ? Number(row.mrr) : row.mrr,
    addOns: row.addOns ?? [],
    flags: row.flags ?? [],
    latestUsage: row.latestUsage ?? undefined,
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

function normalizeSubscriptionUsage(value: unknown): SubscriptionUsage | null {
  if (!isRecord(value) || typeof value.period !== 'string') {
    return null;
  }

  return {
    period: value.period,
    usageQuantity: typeof value.usageQuantity === 'number' ? value.usageQuantity : null,
    usageCharge: typeof value.usageCharge === 'number' ? value.usageCharge : null,
    startDate: typeof value.startDate === 'string' ? value.startDate : null,
    endDate: typeof value.endDate === 'string' ? value.endDate : null,
    currency: typeof value.currency === 'string' ? value.currency : null,
  };
}

function normalizeSubscriptionContractRow(value: unknown): SubscriptionContractRow | null {
  if (
    !isRecord(value) ||
    typeof value.contractId !== 'string' ||
    typeof value.subscriptionId !== 'string' ||
    typeof value.companyId !== 'string' ||
    typeof value.planCode !== 'string' ||
    typeof value.updatedAt !== 'string' ||
    typeof value.createdAt !== 'string'
  ) {
    return null;
  }

  return {
    contractId: value.contractId,
    subscriptionId: value.subscriptionId,
    companyId: value.companyId,
    companyName: typeof value.companyName === 'string' ? value.companyName : '-',
    companyEmail: typeof value.companyEmail === 'string' ? value.companyEmail : null,
    planCode: value.planCode,
    scheduledPlanCode: typeof value.scheduledPlanCode === 'string' ? value.scheduledPlanCode : null,
    scheduledPlanEffectiveDate:
      typeof value.scheduledPlanEffectiveDate === 'string' ? value.scheduledPlanEffectiveDate : null,
    status: typeof value.status === 'string' ? value.status : 'active',
    currency: typeof value.currency === 'string' ? value.currency : null,
    mrr: typeof value.mrr === 'number' || typeof value.mrr === 'string' ? value.mrr : null,
    billingCycle: typeof value.billingCycle === 'string' ? value.billingCycle : null,
    contractStartDate: typeof value.contractStartDate === 'string' ? value.contractStartDate : null,
    contractEndDate: typeof value.contractEndDate === 'string' ? value.contractEndDate : null,
    nextInvoiceDate: typeof value.nextInvoiceDate === 'string' ? value.nextInvoiceDate : null,
    paymentProvider: typeof value.paymentProvider === 'string' ? value.paymentProvider : null,
    addOns: Array.isArray(value.addOns) ? (value.addOns as SubscriptionContractRow['addOns']) : [],
    latestUsage: normalizeSubscriptionUsage(value.latestUsage),
    flags: Array.isArray(value.flags) ? value.flags.filter((flag): flag is string => typeof flag === 'string') : [],
    metadata: isRecord(value.metadata) ? value.metadata : null,
    stripeCustomerId: typeof value.stripeCustomerId === 'string' ? value.stripeCustomerId : null,
    stripeSubscriptionId: typeof value.stripeSubscriptionId === 'string' ? value.stripeSubscriptionId : null,
    latestInvoiceId: typeof value.latestInvoiceId === 'string' ? value.latestInvoiceId : null,
    stripeLatestInvoiceId: typeof value.stripeLatestInvoiceId === 'string' ? value.stripeLatestInvoiceId : null,
    bankTransferStatus: value.bankTransferStatus as SubscriptionContractRow['bankTransferStatus'],
    bankTransferConfirmedAt:
      typeof value.bankTransferConfirmedAt === 'string' ? value.bankTransferConfirmedAt : null,
    bankTransferMemo: typeof value.bankTransferMemo === 'string' ? value.bankTransferMemo : null,
    updatedAt: value.updatedAt,
    createdAt: value.createdAt,
  };
}

function normalizeSubscriptionContractRows(value: unknown): SubscriptionContractRow[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((row) => {
    const normalized = normalizeSubscriptionContractRow(row);
    return normalized ? [normalized] : [];
  });
}

export function SubscriptionListPage() {
  const { message } = App.useApp();
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState<ContractListFilters>({});
  const [visibleColumns, setVisibleColumns] = usePersistedState<string[]>(
    STORAGE_KEYS.columns,
    Array.from(DEFAULT_COLUMN_KEYS),
  );
  const [savedFilters, setSavedFilters] = usePersistedState<SavedFilterSet[]>(STORAGE_KEYS.filters, []);
  const [selectedFilterId, setSelectedFilterId] = useState<string | null>(null);
  const [isSaveFilterModalOpen, setSaveFilterModalOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detailContractId, setDetailContractId] = useState<string | null>(null);
  const [changePlanTarget, setChangePlanTarget] = useState<SubscriptionContractRow | null>(null);
  const [changePlanForm] = Form.useForm<{ plan: string }>();

  const listQueryInput = useMemo<ContractListFilters | undefined>(() => {
    const next: ContractListFilters = {};
    if (filters.search?.trim()) {
      next.search = filters.search.trim();
    }
    if (filters.status) {
      next.status = filters.status;
    }
    if (filters.planCode) {
      next.planCode = filters.planCode;
    }
    if (filters.billingCycle) {
      next.billingCycle = filters.billingCycle;
    }
    if (filters.paymentProvider) {
      next.paymentProvider = filters.paymentProvider;
    }
    return Object.keys(next).length > 0 ? next : undefined;
  }, [filters]);

  const listQuery = trpc.subscriptionContractAdmin.list.useQuery(listQueryInput, {
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  const detailQuery = trpc.subscriptionContractAdmin.get.useQuery(
    detailContractId ? { contractId: detailContractId } : undefined,
    {
      enabled: !!detailContractId,
      staleTime: 30_000,
    },
  );

  const usageQuery = trpc.subscriptionContractAdmin.usage.useQuery(
    detailContractId ? { contractId: detailContractId } : undefined,
    {
      enabled: !!detailContractId,
      staleTime: 60_000,
    },
  );

  const pauseMutation = trpc.subscriptionContractAdmin.pause.useMutation({
    onSuccess: (result) => {
      message.success(result?.status === 'canceling' ? '停止予約を更新しました' : '契約を再開しました');
      listQuery.refetch();
      if (detailContractId) {
        detailQuery.refetch();
        usageQuery.refetch();
      }
    },
    onError: (error) => {
      message.error(error?.message ?? '契約の停止に失敗しました');
    },
  });

  const changePlanMutation = trpc.subscriptionContractAdmin.changePlan.useMutation({
    onSuccess: (result) => {
      if (result.changeType === 'downgrade') {
        message.success('プラン変更を予約しました。次回請求日から適用されます。');
      } else {
        message.success('プランを変更しました');
      }
      listQuery.refetch();
      if (detailContractId) {
        detailQuery.refetch();
      }
      setChangePlanTarget(null);
    },
    onError: (error) => {
      message.error(error?.message ?? 'プラン変更に失敗しました');
    },
  });

  const createInvoiceMutation = trpc.subscriptionContractAdmin.createInvoice.useMutation({
    onSuccess: (result) => {
      if (result.hostedInvoiceUrl) {
        message.success('請求書を発行しました（Stripeで確認できます）');
      } else {
        message.success('請求書を発行しました');
      }
      listQuery.refetch();
      if (detailContractId) {
        detailQuery.refetch();
      }
    },
    onError: (error) => {
      message.error(error?.message ?? '請求書の発行に失敗しました');
    },
  });

  const dataSource = useMemo<SubscriptionContractRow[]>(() => {
    return normalizeSubscriptionContractRows(listQuery.data).map(mapRow);
  }, [listQuery.data]);
  const detailContract = useMemo(
    () => {
      const normalized = normalizeSubscriptionContractRow(detailQuery.data);
      return normalized ? mapRow(normalized) : null;
    },
    [detailQuery.data]
  );

  const summary = useMemo(() => calculateSummary(dataSource), [dataSource]);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      setFilters((prev) => ({ ...prev, search: value || undefined }));
    },
    [],
  );

  const resetFilters = () => {
    setFilters({});
    setSearchInput('');
    setSelectedFilterId(null);
  };

  const handleSaveFilter = () => {
    const trimmed = filterName.trim();
    if (!trimmed) {
      message.warning('フィルター名を入力してください');
      return;
    }
    const newFilter: SavedFilterSet = {
      id: (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `filter-${Date.now()}`),
      name: trimmed,
      createdAt: new Date().toISOString(),
      filters,
    };
    const next = [...savedFilters.filter((item) => item.name !== trimmed), newFilter];
    setSavedFilters(next);
    setSelectedFilterId(newFilter.id);
    setSaveFilterModalOpen(false);
    setFilterName('');
    message.success('フィルターを保存しました');
  };

  const applySavedFilter = (id: string) => {
    const item = savedFilters.find((filter) => filter.id === id);
    if (!item) {
      return;
    }
    setSelectedFilterId(id);
    setFilters({ ...item.filters });
    setSearchInput(item.filters.search ?? '');
  };

  const deleteSavedFilter = (id: string) => {
    const next = savedFilters.filter((filter) => filter.id !== id);
    setSavedFilters(next);
    if (selectedFilterId === id) {
      setSelectedFilterId(null);
    }
    message.success('保存済みフィルターを削除しました');
  };

  const handleOpenDrawer = useCallback((record: SubscriptionContractRow) => {
    setDetailContractId(record.contractId);
    setDrawerOpen(true);
  }, []);

  const handlePause = useCallback((record: SubscriptionContractRow) => {
    pauseMutation.mutate({ contractId: record.contractId, cancelAtPeriodEnd: true });
  }, [pauseMutation]);

  const handleChangePlanStart = useCallback((record: SubscriptionContractRow) => {
    setChangePlanTarget(record);
    changePlanForm.setFieldsValue({ plan: record.planCode });
  }, [changePlanForm]);

  const submitChangePlan = async () => {
    const values = await changePlanForm.validateFields();
    if (!changePlanTarget) {
      return;
    }
    changePlanMutation.mutate({ contractId: changePlanTarget.contractId, planCode: values.plan });
  };

  const handleCreateInvoice = useCallback((record: SubscriptionContractRow) => {
    createInvoiceMutation.mutate({ contractId: record.contractId });
  }, [createInvoiceMutation]);

  const visibleColumnSet = useMemo(() => new Set(visibleColumns), [visibleColumns]);

  const columns = useMemo<ColumnsType<SubscriptionContractRow>>(() => {
    const baseColumns: Record<string, ColumnsType<SubscriptionContractRow>[number]> = {
      company: {
        title: '会社',
        dataIndex: 'companyName',
        key: 'company',
        render: (_: unknown, record) => (
          <Space direction="vertical" size={0}>
            <Text strong>{record.companyName}</Text>
            {record.companyEmail && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {record.companyEmail}
              </Text>
            )}
          </Space>
        ),
      },
      plan: {
        title: 'プラン',
        dataIndex: 'planCode',
        key: 'plan',
        render: (_: unknown, record: SubscriptionContractRow) => (
          <Space direction="vertical" size={0}>
            <Tag color="geekblue">{PLAN_LABELS[record.planCode] ?? record.planCode.toUpperCase()}</Tag>
            {record.scheduledPlanCode && (
              <Tooltip title={record.scheduledPlanEffectiveDate ? `${formatDateShort(record.scheduledPlanEffectiveDate)} から適用` : '次回請求日から適用'}>
                <Tag color="orange" style={{ fontSize: 11 }}>
                  → {PLAN_LABELS[record.scheduledPlanCode] ?? record.scheduledPlanCode.toUpperCase()}
                </Tag>
              </Tooltip>
            )}
          </Space>
        ),
      },
      status: {
        title: 'ステータス',
        dataIndex: 'status',
        key: 'status',
        render: (status: string) => {
          const label = STATUS_LABELS[status] ?? status;
          const color = status === 'active' ? 'green' : status === 'past_due' ? 'volcano' : 'blue';
          return <Tag color={color}>{label}</Tag>;
        },
      },
      mrr: {
        title: 'MRR',
        dataIndex: 'mrr',
        key: 'mrr',
        align: 'right',
        render: (mrr: number | string | null | undefined) => formatCurrency(mrr ?? 0),
      },
      billingCycle: {
        title: '課金サイクル',
        dataIndex: 'billingCycle',
        key: 'billingCycle',
        render: (cycle: string | null | undefined) => BILLING_CYCLE_LABELS[cycle ?? ''] ?? cycle ?? '-',
      },
      nextInvoice: {
        title: '次回請求日',
        dataIndex: 'nextInvoiceDate',
        key: 'nextInvoice',
        render: (value: string | Date | null | undefined) => formatDateShort(value),
      },
      paymentProvider: {
        title: '支払いプロバイダー',
        dataIndex: 'paymentProvider',
        key: 'paymentProvider',
        render: (provider: string | null | undefined) => PAYMENT_PROVIDER_LABELS[provider ?? ''] ?? provider ?? '-',
      },
      usage: {
        title: '最新利用量',
        dataIndex: 'latestUsage',
        key: 'usage',
        render: (usage: SubscriptionUsage | null | undefined) => {
          if (!usage) return '-';
          return (
            <Space direction="vertical" size={0}>
              <Text>{`${usage.usageQuantity ?? 0} / ${formatCurrency(usage.usageCharge ?? 0)}`}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{usage.period}</Text>
            </Space>
          );
        },
      },
      flags: {
        title: 'フラグ',
        dataIndex: 'flags',
        key: 'flags',
        render: (flags: string[] | undefined) =>
          (flags && flags.length > 0 ? flags.map((flag) => <Tag key={flag} color="purple">{flag}</Tag>) : '-'),
      },
      updatedAt: {
        title: '最終更新',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        render: (value: string | Date) => formatDate(value),
      },
    };

    const dynamicColumns = DEFAULT_COLUMN_KEYS.filter((key) => visibleColumnSet.has(key)).map((key) => baseColumns[key]);

    const actionColumn: ColumnsType<SubscriptionContractRow>[number] = {
      title: '操作',
      key: 'actions',
      fixed: 'right',
      width: 200,
      render: (_: unknown, record) => (
        <Space size="small">
          <Tooltip title="詳細">
            <Button icon={<EyeOutlined />} onClick={() => handleOpenDrawer(record)} />
          </Tooltip>
          <Tooltip title="プラン変更">
            <Button icon={<SwapOutlined />} onClick={() => handleChangePlanStart(record)} />
          </Tooltip>
          <Tooltip title="停止予約">
            <Button icon={<PauseCircleOutlined />} onClick={() => handlePause(record)} loading={pauseMutation.isPending} />
          </Tooltip>
          <Tooltip title="請求書発行">
            <Button icon={<FileAddOutlined />} onClick={() => handleCreateInvoice(record)} loading={createInvoiceMutation.isPending} />
          </Tooltip>
        </Space>
      ),
    };

    return [...dynamicColumns, actionColumn].filter(Boolean);
  }, [visibleColumnSet, pauseMutation.isPending, createInvoiceMutation.isPending, handleOpenDrawer, handleChangePlanStart, handlePause, handleCreateInvoice]);

  const columnSelection = (
    <Checkbox.Group
      value={visibleColumns}
      onChange={(values) => {
        const next = (values as string[]).length > 0 ? (values as string[]) : Array.from(DEFAULT_COLUMN_KEYS);
        setVisibleColumns(next);
      }}
    >
      <Space direction="vertical">
        {COLUMN_OPTIONS.map((option) => (
          <Checkbox key={option.key} value={option.key}>
            {option.label}
          </Checkbox>
        ))}
      </Space>
    </Checkbox.Group>
  );

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card>
        <Row gutter={16}>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="総契約" value={summary.total} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="稼働中" value={summary.active} valueStyle={{ color: '#389e0d' }} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="支払遅延" value={summary.pastDue} valueStyle={{ color: '#cf1322' }} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="要入金確認" value={summary.pendingBank} valueStyle={{ color: '#faad14' }} />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Statistic title="要フォロー" value={summary.flagged} valueStyle={{ color: '#722ed1' }} />
          </Col>
        </Row>
      </Card>

      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={10} lg={8}>
              <Search
                placeholder="会社名 / 契約IDなどを検索"
                allowClear
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onSearch={handleSearch}
                enterButton
              />
            </Col>
            <Col xs={24} md={14} lg={16}>
              <Space wrap>
                <Select
                  placeholder="ステータス"
                  allowClear
                  style={{ width: 160 }}
                  value={filters.status}
                  onChange={(value) => setFilters((prev) => ({ ...prev, status: value ?? undefined }))}
                >
                  {STATUS_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
                </Select>
                <Select
                  placeholder="プラン"
                  allowClear
                  style={{ width: 160 }}
                  value={filters.planCode}
                  onChange={(value) => setFilters((prev) => ({ ...prev, planCode: value ?? undefined }))}
                >
                  {AVAILABLE_PLAN_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
                <Select
                  placeholder="課金サイクル"
                  allowClear
                  style={{ width: 160 }}
                  value={filters.billingCycle}
                  onChange={(value) => setFilters((prev) => ({ ...prev, billingCycle: value ?? undefined }))}
                >
                  {BILLING_CYCLE_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
                <Select
                  placeholder="支払いプロバイダー"
                  allowClear
                  style={{ width: 180 }}
                  value={filters.paymentProvider}
                  onChange={(value) => setFilters((prev) => ({ ...prev, paymentProvider: value ?? undefined }))}
                >
                  {PAYMENT_PROVIDER_OPTIONS.map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
                <Button onClick={resetFilters}>フィルターリセット</Button>
              </Space>
            </Col>
          </Row>

          <Row justify="space-between" align="middle" gutter={[16, 16]}>
            <Col>
              <Space wrap>
                <Button
                  icon={<SaveOutlined />}
                  onClick={() => {
                    setFilterName('');
                    setSaveFilterModalOpen(true);
                  }}
                  disabled={!filters.search && !filters.status && !filters.planCode && !filters.billingCycle && !filters.paymentProvider}
                >
                  フィルター保存
                </Button>
                <Select
                  placeholder="保存済みフィルター"
                  allowClear
                  style={{ width: 220 }}
                  value={selectedFilterId ?? undefined}
                  onChange={(value) => {
                    if (!value) {
                      setSelectedFilterId(null);
                      return;
                    }
                    applySavedFilter(value);
                  }}
                >
                  {savedFilters.map((filter) => (
                    <Option key={filter.id} value={filter.id}>
                      {filter.name}
                    </Option>
                  ))}
                </Select>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!selectedFilterId}
                  onClick={() => selectedFilterId && deleteSavedFilter(selectedFilterId)}
                >
                  保存フィルター削除
                </Button>
              </Space>
            </Col>
            <Col>
              <Space>
                <Popover content={columnSelection} trigger="click" placement="bottomRight">
                  <Button icon={<SettingOutlined />}>表示列</Button>
                </Popover>
                <Button icon={<ReloadOutlined />} onClick={() => listQuery.refetch()} loading={listQuery.isFetching}>
                  再読込
                </Button>
              </Space>
            </Col>
          </Row>

          <Table<SubscriptionContractRow>
            columns={columns}
            dataSource={dataSource}
            rowKey="contractId"
            loading={listQuery.isLoading}
            scroll={{ x: 1200 }}
            onRow={(record) => ({
              onClick: () => handleOpenDrawer(record),
            })}
            pagination={{ pageSize: 20, showSizeChanger: false }}
          />

          {listQuery.data && listQuery.data.length === 0 && (
            <Alert
              type="info"
              icon={<InfoCircleOutlined />}
              message="条件に一致する契約が見つかりません"
              showIcon
            />
          )}
        </Space>
      </Card>

      <Drawer
        title="契約詳細"
        width={520}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => detailQuery.refetch()} loading={detailQuery.isLoading}>
              更新
            </Button>
            {detailContractId && (
              <Button icon={<PauseCircleOutlined />} onClick={() => detailContractId && pauseMutation.mutate({ contractId: detailContractId, cancelAtPeriodEnd: true })}>
                停止予約
              </Button>
            )}
          </Space>
        }
      >
        {detailQuery.isLoading && <Text>読み込み中...</Text>}
        {detailQuery.data && (
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="契約ID">{detailQuery.data.contractId}</Descriptions.Item>
              <Descriptions.Item label="会社">
                <Space direction="vertical" size={0}>
                  <Text strong>{detailQuery.data.companyName}</Text>
                  {detailQuery.data.companyEmail && (
                    <Text type="secondary">{detailQuery.data.companyEmail}</Text>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="プラン">
                <Space direction="vertical" size={0}>
                  <span>{PLAN_LABELS[detailQuery.data.planCode] ?? detailQuery.data.planCode}</span>
                  {detailQuery.data.scheduledPlanCode && (
                    <Text type="warning" style={{ fontSize: 12 }}>
                      → {PLAN_LABELS[detailQuery.data.scheduledPlanCode] ?? detailQuery.data.scheduledPlanCode}
                      {detailQuery.data.scheduledPlanEffectiveDate && (
                        <> ({formatDateShort(detailQuery.data.scheduledPlanEffectiveDate)} から)</>
                      )}
                    </Text>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="ステータス">{STATUS_LABELS[detailQuery.data.status] ?? detailQuery.data.status}</Descriptions.Item>
              <Descriptions.Item label="MRR">{formatCurrency(detailQuery.data.mrr ?? 0)}</Descriptions.Item>
              <Descriptions.Item label="課金サイクル">{BILLING_CYCLE_LABELS[detailQuery.data.billingCycle ?? ''] ?? detailQuery.data.billingCycle ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="契約期間">
                {detailQuery.data.contractStartDate ? formatDateShort(detailQuery.data.contractStartDate) : '-'}
                {' / '}
                {detailQuery.data.contractEndDate ? formatDateShort(detailQuery.data.contractEndDate) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="次回請求日">{formatDateShort(detailQuery.data.nextInvoiceDate)}</Descriptions.Item>
              <Descriptions.Item label="支払いプロバイダー">{PAYMENT_PROVIDER_LABELS[detailQuery.data.paymentProvider ?? ''] ?? detailQuery.data.paymentProvider ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="Stripe サブスクリプション">
                {detailQuery.data.stripeSubscriptionId ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="備考">
                <Space size={[4, 4]} wrap>
                  {(detailQuery.data.flags ?? []).map((flag) => (
                    <Tag key={flag} color="purple">{flag}</Tag>
                  ))}
                  {(detailQuery.data.flags ?? []).length === 0 && <Text>-</Text>}
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Card size="small" title="利用状況">
              {usageQuery.isLoading && <Text>利用データを取得しています...</Text>}
              {usageQuery.data?.usage ? (
                <Space direction="vertical">
                  <Text>{`対象期間: ${usageQuery.data.usage.period}`}</Text>
                  <Text>{`利用量: ${usageQuery.data.usage.usageQuantity}`}</Text>
                  <Text>{`課金額: ${formatCurrency(usageQuery.data.usage.usageCharge)}`}</Text>
                </Space>
              ) : (
                <Text>最新の利用データがありません</Text>
              )}
              <Divider />
              <Space>
                <Button icon={<ReloadOutlined />} onClick={() => usageQuery.refetch()} loading={usageQuery.isFetching}>
                  利用データ更新
                </Button>
                <Button icon={<FileAddOutlined />} onClick={() => detailContract && handleCreateInvoice(detailContract)} loading={createInvoiceMutation.isPending}>
                  請求書発行
                </Button>
                <Button icon={<SwapOutlined />} onClick={() => detailContract && handleChangePlanStart(detailContract)}>
                  プラン変更
                </Button>
              </Space>
            </Card>
          </Space>
        )}
      </Drawer>

      <Modal
        title="フィルターを保存"
        open={isSaveFilterModalOpen}
        onCancel={() => {
          setSaveFilterModalOpen(false);
          setFilterName('');
        }}
        onOk={handleSaveFilter}
        okText="保存"
        cancelText="キャンセル"
      >
        <Input
          placeholder="フィルター名"
          value={filterName}
          onChange={(event) => setFilterName(event.target.value)}
          maxLength={40}
        />
      </Modal>

      <Modal
        title={changePlanTarget ? `${changePlanTarget.companyName} のプラン変更` : 'プラン変更'}
        open={!!changePlanTarget}
        onCancel={() => setChangePlanTarget(null)}
        onOk={submitChangePlan}
        okButtonProps={{ loading: changePlanMutation.isPending }}
        destroyOnHidden
      >
        <Form form={changePlanForm} layout="vertical">
          <Form.Item name="plan" label="新しいプラン" rules={[{ required: true, message: 'プランを選択してください' }]}>
            <Select placeholder="プランを選択">
              {AVAILABLE_PLAN_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}

